
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * FeaturesToggle struct
 */
class FeaturesToggle {
  /**
   * Initialize struct
   * @param {!boolean=} multipleDashboards
   * @param {!boolean=} socialTradingAccountsManipulation
   * @constructor
   */
  constructor (multipleDashboards = false, socialTradingAccountsManipulation = false) {
    this.MultipleDashboards = multipleDashboards
    this.SocialTradingAccountsManipulation = socialTradingAccountsManipulation
  }

  /**
   * Copy struct (shallow copy)
   * @this {!FeaturesToggle}
   * @param {!FeaturesToggle} other Other struct
   * @returns {!FeaturesToggle} This struct
   */
  copy (other) {
    if (other.MultipleDashboards != null) {
      this.MultipleDashboards = other.MultipleDashboards
    } else {
      this.MultipleDashboards = null
    }
    if (other.SocialTradingAccountsManipulation != null) {
      this.SocialTradingAccountsManipulation = other.SocialTradingAccountsManipulation
    } else {
      this.SocialTradingAccountsManipulation = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!FeaturesToggle}
   * @returns {!FeaturesToggle} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new FeaturesToggleModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new FeaturesToggleModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!FeaturesToggle}
   * @param {!FeaturesToggle} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof FeaturesToggle)) {
      throw new TypeError('Instance of FeaturesToggle is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!FeaturesToggle}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      MultipleDashboards: ((this.MultipleDashboards != null) ? this.MultipleDashboards : null),
      SocialTradingAccountsManipulation: ((this.SocialTradingAccountsManipulation != null) ? this.SocialTradingAccountsManipulation : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return FeaturesToggle.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!FeaturesToggle} other Object value
   * @returns {!FeaturesToggle} Created struct
   */
  static fromObject (other) {
    return new FeaturesToggle().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!FeaturesToggle}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return FeaturesToggle.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!FeaturesToggle}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 198
  }
}

export { FeaturesToggle };

/**
 * Fast Binary Encoding FeaturesToggle field model
 */
class FieldModelFeaturesToggle extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.MultipleDashboards = new fbe.FieldModelBool(buffer, 4 + 4)
    this.SocialTradingAccountsManipulation = new fbe.FieldModelBool(buffer, this.MultipleDashboards.fbeOffset + this.MultipleDashboards.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelFeaturesToggle}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelFeaturesToggle}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.MultipleDashboards.fbeSize + this.SocialTradingAccountsManipulation.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelFeaturesToggle}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.MultipleDashboards.fbeExtra + this.SocialTradingAccountsManipulation.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelFeaturesToggle}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelFeaturesToggle.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelFeaturesToggle}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 198
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelFeaturesToggle}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelFeaturesToggle}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.MultipleDashboards.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MultipleDashboards.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MultipleDashboards.fbeSize

    if ((fbeCurrentSize + this.SocialTradingAccountsManipulation.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SocialTradingAccountsManipulation.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SocialTradingAccountsManipulation.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelFeaturesToggle}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelFeaturesToggle}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelFeaturesToggle}
   * @param {!FeaturesToggle} fbeValue Default value, defaults is new FeaturesToggle()
   * @returns {!FeaturesToggle} FeaturesToggle value
   */
  get (fbeValue = new FeaturesToggle()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelFeaturesToggle}
   * @param {!FeaturesToggle} fbeValue FeaturesToggle value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.MultipleDashboards.fbeSize) <= fbeStructSize) {
      fbeValue.MultipleDashboards = this.MultipleDashboards.get(false)
    } else {
      fbeValue.MultipleDashboards = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MultipleDashboards.fbeSize

    if ((fbeCurrentSize + this.SocialTradingAccountsManipulation.fbeSize) <= fbeStructSize) {
      fbeValue.SocialTradingAccountsManipulation = this.SocialTradingAccountsManipulation.get(false)
    } else {
      fbeValue.SocialTradingAccountsManipulation = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SocialTradingAccountsManipulation.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelFeaturesToggle}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelFeaturesToggle}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelFeaturesToggle}
   * @param {!FeaturesToggle} fbeValue FeaturesToggle value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelFeaturesToggle}
   * @param {FeaturesToggle} fbeValue FeaturesToggle value
   */
  setFields (fbeValue) {
    this.MultipleDashboards.set(fbeValue.MultipleDashboards)
    this.SocialTradingAccountsManipulation.set(fbeValue.SocialTradingAccountsManipulation)
  }
}

export { FieldModelFeaturesToggle };

/**
 * Fast Binary Encoding FeaturesToggle model
 */
class FeaturesToggleModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelFeaturesToggle(this.buffer, 4)
  }

  /**
   * Get the FeaturesToggle model
   * @this {!FeaturesToggleModel}
   * @returns {!FieldModelFeaturesToggle} model FeaturesToggle model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!FeaturesToggleModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!FeaturesToggleModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return FeaturesToggleModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!FeaturesToggleModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelFeaturesToggle.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!FeaturesToggleModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!FeaturesToggleModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!FeaturesToggleModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!FeaturesToggleModel}
   * @param {!FeaturesToggle} value FeaturesToggle value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!FeaturesToggleModel}
   * @param {!FeaturesToggle} value FeaturesToggle value, defaults is new FeaturesToggle()
   * @return {!object} Deserialized FeaturesToggle value and its size
   */
  deserialize (value = new FeaturesToggle()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new FeaturesToggle(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new FeaturesToggle(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!FeaturesToggleModel}
   * @param {!number} prev Previous FeaturesToggle model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { FeaturesToggleModel };
